import { VaultCrypto } from '@password-manager/crypto';
import { WebCryptoProvider } from '../crypto/WebCryptoProvider';

function parseJsonSafe(val) {
  if (!val) return null;
  if (typeof val === 'object') return val;
  try {
    return JSON.parse(val);
  } catch {
    return null;
  }
}

/**
 * Decrypts a shared vault item grant.
 * @param {object} grant - The share grant object (from getShareGrant or listReceivedGrants)
 * @param {string|null} [vaultKey] - Optional master vault key if current user is owner
 * @returns {Promise<object>} Decrypted item plaintext object { title, username, password, url, notes, ... }
 */
export async function decryptShareGrant(grant, vaultKey = null) {
  if (!grant) {
    throw new Error('No grant provided to decryptShareGrant');
  }

  const encItemKey = parseJsonSafe(grant.encryptedItemKey || grant.encrypted_item_key);
  const encData = parseJsonSafe(grant.encryptedData || grant.encrypted_data);

  if (!encData) {
    throw new Error('Missing encrypted data in share grant');
  }

  const crypto = new VaultCrypto(WebCryptoProvider);
  const token = encItemKey?.shareToken || encItemKey?.token || grant.shareToken || grant.token;
  const itemId = grant.vaultItemId || grant.vault_item_id || grant.id;

  // 1. Decrypt using share token if available
  if (token && encItemKey?.ct) {
    try {
      const itemKey = await crypto.unwrapItemKey(encItemKey, token, grant.id, 'share-grant');
      const raw = await WebCryptoProvider.decryptAESGCM(
        itemKey,
        encData.iv,
        encData.ct,
        encData.mac,
        `vault-item:${itemId}`
      );
      return typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch (tokenErr) {
      console.warn('Share decryption via shareToken failed:', tokenErr);
    }
  }

  // 2. Fallback: if user is owner and has vaultKey
  if (vaultKey) {
    try {
      const raw = await crypto.decryptItem(encData, vaultKey, itemId);
      return typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch (vaultKeyErr) {
      console.warn('Share decryption via vaultKey failed:', vaultKeyErr);
    }
  }

  throw new Error('Unable to decrypt share grant (missing or invalid decryption key)');
}
